import { Case, upper, parseDate, Segment } from './util';
import makePrice, { PriceArg } from './Price';
import * as $ from 'jquery';
import { ProductData } from 'lib/ecommerce/Product';
import { Texts } from 'lib/ecommerce/Tc.Module.EcommerceProductDataHub';

interface ActivatableModel {
	validFrom? : string;
	validTo? : string;
	active? : boolean;
}

abstract class Activatable {
	public isOn(date : Date = new Date()) {
		const from = this.getStartDate();
		if(from && from > date) {
			return false;
		}

		const to = this.getEndDate();
		if(to && to < date) {
			return false;
		}

		return true;
	}

	public isValid(date : Date = new Date()) {
		if(!this.isActive()) {
			return false;
		}

		return this.isOn(date);
	}

	public isActive() {
		// FIXME NEC: Check again when this flag is deployed if we actually need to check for presence.
		return !('active' in this.getModel()) || this.getModel().active;
	}

	public getStartDate() {
		if(!this.getModel().validFrom) {
			return null;
		}

		return parseDate(this.getModel().validFrom);
	}

	public getEndDate() {
		if(!this.getModel().validTo) {
			return null;
		}

		return parseDate(this.getModel().validTo);
	}

	protected abstract getModel() : ActivatableModel;
}

type PromotionType = 'DISCOUNT' | 'BESTSELLER' | 'PROMOTION' | 'FREETEXT' | 'NEW';

export interface PromotionData extends RawPromotionData, RawPromotionBundleData {
	promotionCode? : string;
	callout? : boolean;
}

export const PROMOTION_PRIORITY : PromotionType[] = ['DISCOUNT', 'BESTSELLER', 'PROMOTION', 'FREETEXT', 'NEW'];

export class Promotion extends Activatable {
	private promotion : PromotionData;
	constructor(promotion : PromotionData) {
		super();
		this.promotion = promotion;
	}

	public getId() {
		return this.promotion.promotionCode || this.promotion.code;
	}
	public getBundleId() {
		return this.promotion.code;
	}
	public getName() {
		return this.promotion.name;
	}
	public getDescription() {
		return this.promotion.description;
	}
	public getSlogan() {
		return this.promotion.slogan;
	}
	public getCases() {
		return (this.promotion.cases || []).map(upper);
	}
	public getSubscriptionId() {
		return this.promotion.subscriptionId;
	}
	public getDurations() {
		return (this.promotion.durations || []).map(Number);
	}
	public isCallout() {
		return this.promotion.callout;
	}
	public getType() {
		return upper(this.promotion.type) as PromotionType;
	}

	/**
	 * Checks if the promotion is applicable for a certain configuration
	 * If an argument is omitted, return only true if the promotion is unconditional on that argument.
	 */
	public matches(subscriptionId? : string, duration? : string | number, kaze? : Case) {
		if(!this.isValid()) {
			// Never apply promotions whose dates don’t match
			return false;
		}

		if(this.getSubscriptionId() && subscriptionId !== this.getSubscriptionId()) {
			// Subscription needs to match but didn’t
			return false;
		}

		if(this.getDurations().length && $.inArray(Number(duration), this.getDurations()) === -1) {
			// Duration needs to be set but was not found in conditions
			return false;
		}

		if(this.getCases().length && $.inArray(upper(kaze || ''), this.getCases()) === -1) {
			// Case was expected but did not match
			return false;
		}

		return true;
	}

	// From Activatable
	protected getModel() : ActivatableModel {
		return this.promotion;
	}
}

export interface DiscountData extends ActivatableModel {
	oneTimePrice? : number;
	oneTimePricePercentage? : number;
}

export class Discount extends Activatable {
	private discountSpecialOffer : DiscountData;

	constructor(discountSpecialOffer : DiscountData) {
		super();
		this.discountSpecialOffer = discountSpecialOffer;
	}

	public getPrice(fullPrice : PriceArg) {
		if(this.discountSpecialOffer.oneTimePrice) {
			return makePrice(this.discountSpecialOffer.oneTimePrice);
		}

		// Absolute price
		const price = makePrice(fullPrice);

		if(this.discountSpecialOffer.oneTimePricePercentage) {
			// FIXME NEC: Assuming oneTimePricePercentage is an int betwen 0…100 signifiyin the discount.
			return makePrice(price.value() * (1 - (this.discountSpecialOffer.oneTimePricePercentage / 100)));
		}

		return price;
	}

	// From Activatable
	protected getModel() : ActivatableModel {
		return this.discountSpecialOffer;
	}
}

export const REBATE_EXEMPT_CATEGORIES = ['MobileLike.Handy.Smartphone', 'MobileLike.Handy', 'MobileLike.Tablets'];

export interface RawPromotionBundleData {
	code : string;
	name : string;
	description? : string;
	slogan? : string;
	bundleEntries? : any[];
	maxSelectable? : number;
}

export interface RawPromotionData extends ActivatableModel {
	code : string;
	productId? : string;
	subscriptionId? : string;
	segments? : Segment[];
	cases? : Case[];
	durations? : (number | string)[];
	type : string;
	description? : string;
	extendedDiscription? : string;
	calloutText? : string;
	reasonText? : string;
	promoBundles? : RawPromotionBundleData[];
}

export function normalizePromotions(promotions : RawPromotionData[] = []) : PromotionData[] {
	// Flatten promotions ⇒ promotionBundles

	const result = (promotions || []).map(promotion => {
		return (promotion.promoBundles || []).map(promoBundle => {
			return {
				...promotion,
				...promoBundle,
				...{promotionCode: promotion.code},
			};
		});
	});

	// Flatten arrays in array
	return ([] as PromotionData[]).concat(...result);
}

export function synthesizeCallouts(
	product : ProductData,
	bestseller : string,
	texts : Texts,
	existingPromotions : PromotionData[],
	discountPrice : Discount | null = null,
) : PromotionData[] {
	const result : PromotionData[] = [];

	// Handle bestseller flag
	const isBestseller = (bestseller || '').indexOf(`,${product.code},`) > -1;
	if(isBestseller) {
		// Artificially add the bestseller promotion to the list of promotions
		result.push({
			callout: true,
			code: 'bestseller',
			name: texts.bestseller,
			type: 'bestseller',
		});
	}

	// Handle callout property
	if(product.callout && product.callout.code && product.callout.code !== 'NONE') {
		// Artificially add the callout promotion (if any) to the list of promotions
		result.push({
			callout: true,
			code: product.callout.code,
			name: product.callout.name || product.callout.code,
			type: product.callout.code,
		});
	}

	// Handle discount
	if(discountPrice && discountPrice.isValid()) {
		result.push({code: 'discount', name: texts.discountflagtext, callout: true, type: 'discount'});
	}

	// Consider all promotions in existingPromotions to be non-badge.
	for(const promo of existingPromotions) {
		// Use cases, durations and subscriptionId from original promo (if available)
		result.push({
			...promo,
			...{
				callout: true,
				name: texts.promotionflagtext,
				type: 'promotion',
			},
		});
	}

	return result;
}



// WEBPACK FOOTER //
// typescript://../clientlibs/js/lib/ecommerce/Promotion.ts